import type { ReactNode } from 'react'
import { Form, Input } from '@arco-design/web-react'
import SettingSectionCard from '../SettingSectionCard'
import SensitiveInput from '../SensitiveInput'
import InstantSwitch from '../InstantSwitch'
import type { OptionsAPI } from '../useOptions'
import { FIXED_PUBLIC_SITE_ORIGIN } from '@/utils/commercialSite'

function OAuthCard({
  title,
  enabledKey,
  keys,
  options,
  children,
}: {
  title: string
  enabledKey: string
  keys: string[]
  options: OptionsAPI
  children: ReactNode
}) {
  return (
    <SettingSectionCard title={title} keys={[enabledKey, ...keys]} options={options}>
      <Form layout="vertical">
        <Form.Item label="启用">
          <InstantSwitch options={options} optionKey={enabledKey} />
        </Form.Item>
        <div className="grid md:grid-cols-2 gap-3">{children}</div>
      </Form>
    </SettingSectionCard>
  )
}

export default function OAuthProvidersSection({ options }: { options: OptionsAPI }) {
  const { get, patch } = options
  const origin = get('ServerAddress', FIXED_PUBLIC_SITE_ORIGIN).replace(/\/$/, '')

  return (
    <>
      <OAuthCard title="GitHub OAuth" enabledKey="GitHubOAuthEnabled" keys={['GitHubClientId', 'GitHubClientSecret']} options={options}>
        <Form.Item label="Client ID" extra={`回调 URL：${origin}/oauth/github`}>
          <Input value={get('GitHubClientId')} onChange={(v) => patch({ GitHubClientId: v })} />
        </Form.Item>
        <Form.Item label="Client Secret">
          <SensitiveInput value={get('GitHubClientSecret')} onChange={(v) => patch({ GitHubClientSecret: v })} />
        </Form.Item>
      </OAuthCard>

      <OAuthCard
        title="Discord OAuth"
        enabledKey="discord.enabled"
        keys={['discord.client_id', 'discord.client_secret']}
        options={options}
      >
        <Form.Item label="Client ID" extra={`回调 URL：${origin}/oauth/discord`}>
          <Input value={get('discord.client_id')} onChange={(v) => patch({ 'discord.client_id': v })} />
        </Form.Item>
        <Form.Item label="Client Secret">
          <SensitiveInput value={get('discord.client_secret')} onChange={(v) => patch({ 'discord.client_secret': v })} />
        </Form.Item>
      </OAuthCard>

      <OAuthCard
        title="OIDC"
        enabledKey="oidc.enabled"
        keys={[
          'oidc.well_known',
          'oidc.client_id',
          'oidc.client_secret',
          'oidc.authorization_endpoint',
          'oidc.token_endpoint',
          'oidc.user_info_endpoint',
        ]}
        options={options}
      >
        <Form.Item label="Well-Known URL" extra={`重定向 URL：${origin}/oauth/oidc`}>
          <Input value={get('oidc.well_known')} onChange={(v) => patch({ 'oidc.well_known': v })} />
        </Form.Item>
        <Form.Item label="Client ID">
          <Input value={get('oidc.client_id')} onChange={(v) => patch({ 'oidc.client_id': v })} />
        </Form.Item>
        <Form.Item label="Client Secret">
          <SensitiveInput value={get('oidc.client_secret')} onChange={(v) => patch({ 'oidc.client_secret': v })} />
        </Form.Item>
        <Form.Item label="Authorization Endpoint">
          <Input value={get('oidc.authorization_endpoint')} onChange={(v) => patch({ 'oidc.authorization_endpoint': v })} />
        </Form.Item>
        <Form.Item label="Token Endpoint">
          <Input value={get('oidc.token_endpoint')} onChange={(v) => patch({ 'oidc.token_endpoint': v })} />
        </Form.Item>
        <Form.Item label="User Info Endpoint">
          <Input value={get('oidc.user_info_endpoint')} onChange={(v) => patch({ 'oidc.user_info_endpoint': v })} />
        </Form.Item>
      </OAuthCard>

      <OAuthCard
        title="Linux DO OAuth"
        enabledKey="LinuxDOOAuthEnabled"
        keys={['LinuxDOClientId', 'LinuxDOClientSecret', 'LinuxDOMinimumTrustLevel']}
        options={options}
      >
        <Form.Item label="Client ID" extra={`回调 URL：${origin}/oauth/linuxdo`}>
          <Input value={get('LinuxDOClientId')} onChange={(v) => patch({ LinuxDOClientId: v })} />
        </Form.Item>
        <Form.Item label="Client Secret">
          <SensitiveInput value={get('LinuxDOClientSecret')} onChange={(v) => patch({ LinuxDOClientSecret: v })} />
        </Form.Item>
        <Form.Item label="最低信任等级">
          <Input value={get('LinuxDOMinimumTrustLevel')} onChange={(v) => patch({ LinuxDOMinimumTrustLevel: v })} />
        </Form.Item>
      </OAuthCard>

      <OAuthCard
        title="Telegram 登录"
        enabledKey="TelegramOAuthEnabled"
        keys={['TelegramBotToken', 'TelegramBotName']}
        options={options}
      >
        <Form.Item label="Bot Token">
          <SensitiveInput value={get('TelegramBotToken')} onChange={(v) => patch({ TelegramBotToken: v })} />
        </Form.Item>
        <Form.Item label="Bot 名称">
          <Input value={get('TelegramBotName')} onChange={(v) => patch({ TelegramBotName: v })} />
        </Form.Item>
      </OAuthCard>

      <OAuthCard
        title="WeChat Server"
        enabledKey="WeChatAuthEnabled"
        keys={['WeChatServerAddress', 'WeChatServerToken', 'WeChatAccountQRCodeImageURL']}
        options={options}
      >
        <Form.Item label="服务器地址">
          <Input value={get('WeChatServerAddress')} onChange={(v) => patch({ WeChatServerAddress: v })} />
        </Form.Item>
        <Form.Item label="访问凭证">
          <SensitiveInput value={get('WeChatServerToken')} onChange={(v) => patch({ WeChatServerToken: v })} />
        </Form.Item>
        <Form.Item label="公众号二维码图片 URL">
          <Input value={get('WeChatAccountQRCodeImageURL')} onChange={(v) => patch({ WeChatAccountQRCodeImageURL: v })} />
        </Form.Item>
      </OAuthCard>
    </>
  )
}
